import React, { useEffect, useState } from "react";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import { postAddEdit, postDetails } from "../../../../services/postService";
import { useMutation, useQuery } from "@tanstack/react-query";
import Input from "../../../form/Input";
import { toast } from "react-toastify";
import { taxonomyList } from "../../../../services/taxonomyService";
import Editor from "../../../elements/tiptap-editor/Editor";
import Textarea from "../../../form/Textarea";
import Tags from "../../../form/Tags";
import { useNavigate, useParams } from "react-router-dom";
import SelectSingleImage from "../../../form/SelectSingleImage";
import SelectImageGallery from "../../../form/SelectImageGallery";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import DatePicker from "../../../form/DatePicker";
import { supportedLanguages,userLang } from "../../../../helpers";
import LanguageTabs from "../../../elements/LanguageSwitcher";

const validation = {
  // title: { required: true, message: "title is required" },
  // content: { required: true, message: "content is required" },
};

const ManageFreelancer = ({ getResponse = () => {}, close = () => {} }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params?.id;
  const userLang = localStorage.getItem("lang");
  const [tags, setTags] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [coverMedia, setCoverMedia] = useState("");
  const { values, handleChange, handleSubmit, errors, setFieldsValue, formValidate } = useForm({
    validationRules: validation,
  });
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [currentLang, setCurrentLang] = useState(localStorage.getItem("lang") || "en");

  const handleLangChange = (lang) => {
    setCurrentLang(lang);
  };

  const { data: details } = useQuery({
    queryKey: ["freelance-details", id],
    queryFn: async () => {
      const res = await postDetails({ id: id });
      return res?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        paymentType: details?.price?.paymentType,
        rangeType: details?.range?.rangeType,
        ...details,
        content: {
          ...supportedLanguages.reduce((acc, lang) => {
            acc[lang.code] = {
              title: details?.content?.[lang.code]?.title || "",
              content: details?.content?.[lang.code]?.content || "",
              excerpt: details?.content?.[lang.code]?.excerpt || "",
              seo: details?.content?.[lang.code]?.seo || {},
            };
            return acc;
          }, {}),
        },
      });
      setCategories(details?.categories);
      setBudgets(details?.job?.budget?._id);
      setCoverMedia(details?.coverMedia);
      setSelectedMedia(details?.mediaGallery);
      setTags(
        details.tagDetails.map((item) => ({
          label: item?.content?.[userLang]?.title,
          _id: item._id,
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const media = selectedMedia?.reduce((acc, item, index) => {
        acc[`mediaGallery[${index}]`] = item?._id;
        return acc;
      }, {});

      const tag = tags?.reduce((acc, tag, index) => {
        acc[`tags[${index}]`] = tag;
        return acc;
      }, {});

      return await postAddEdit({
        content: val?.content,
        status: val?.status,
        slug:`${val?.content?.[currentLang]?.title.replace(/\s+/g, "-").toLowerCase()}-${Date.now()}`,
        lang:userLang,
        publishDate: val?.publishDate,
        type: "freelance-job",
        jobPaymentType: val?.paymentType,
        jobBudgetRangeType: val?.rangeType,
        coverMedia: coverMedia?._id,
        categories: categories,
        jobBudget: budgets,
        ...(details?._id && { id: details._id }),
        ...media,
        ...tag,
      });
    },

    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        navigate(`/admin/freelancer-jobs`);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { data: budgetData } = useQuery({
    queryKey: ["budget"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "salary-range",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: categoryData } = useQuery({
    queryKey: ["category"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "freelance-job-category",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: tagsData } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "freelance-job-tag",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  return (
    <>
      <form onSubmit={(event) => handleSubmit(event, mutate)}>
        <div className="flex justify-between items-center w-full px-6">
          <div className="font-normal text-lg xl:text-3xl text-primary">Manage Freelancer</div>
          <LanguageTabs languages={supportedLanguages} defaultLang="en" value={currentLang} onChange={handleLangChange} />
          <div className="flex gap-4">
            <Button
              buttonType="submit"
              buttonLabel="Save Freelancer"
              buttonClasses="min-w-28"
              buttonLabelClasses="mx-auto"
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel="Cancel"
              buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
              buttonLabelClasses="mx-auto"
              buttonHasLink={true}
              buttonLink={"/admin/freelancer-jobs"}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6 p-6">
          <div className="col-span-3 space-y-4">
            <Input
              label="Title"
              inputPlaceholder="Enter title"
              // onChange={handleChange}
              onChange={(e) => {
                const newValue = e.target.value;
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      title: newValue,
                    },
                  },
                });
                // formValidate(errors, `content.${currentLang}.title`, newValue);
              }}
              inputName={"title"}
              required={false}
              value={values?.content?.[currentLang]?.title || ""}
              // {...formValidate(errors, "title")}
            />
            <Editor
              label={"Content"}
              height={200}
              value={values?.content?.[currentLang]?.content || ""}
              placeholder={"Write Something"}
              // onChange={(val) =>
              //   handleChange({
              //     target: { name: "content", value: val },
              //   })
              // }
              onChange={(val) => {
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      content: val,
                    },
                  },
                });
                // validate this field immediately
                // formValidate(errors, `content.${currentLang}.content`, newValue);
              }}
              // {...formValidate(errors, "content")}
            />
            <Textarea
              label="Excerpt"
              inputPlaceholder="Write excerpt"
              inputClasses="!h-28"
              inputName="excerpt"
              inputValue={values?.content?.[currentLang]?.excerpt || ""}
              // onChange={handleChange}
              onChange={(e) => {
                const newValue = e.target.value;
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      excerpt: newValue,
                    },
                  },
                });
                // validate this field immediately
                // formValidate(errors, `content.${currentLang}.title`, newValue);
              }}
            />
            <div className="bg-white p-4 rounded-md border border-text-secondary/50">
              <SelectImageGallery label="Gallery" values={selectedMedia} onChange={(newMedia) => setSelectedMedia(newMedia)} isSingle={false} />
            </div>
            <div className="relative col-span-4 mt-5">
              <div className="flex gap-4">
                <Button
                  buttonType="submit"
                  buttonLabel={"Save Freelancer"}
                  buttonClasses={"min-w-28"}
                  buttonLabelClasses={"mx-auto"}
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel={"Cancel"}
                  buttonClasses={"min-w-28 !bg-white !text-primary border !border-primary"}
                  buttonLabelClasses={"mx-auto"}
                  buttonHasLink={true}
                  buttonLink={"/admin/freelancer-jobs"}
                />
              </div>
            </div>
          </div>

          <div className="col-span-1 space-y-6">
            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <SelectSingleImage
                label="Upload Featured Image"
                size="lg"
                value={coverMedia}
                onChange={(img) => setCoverMedia(img)}
                onRemove={() => setCoverMedia(null)}
              />
            </div>
            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <DatePicker
                label={"Publish Date"}
                placeholder={"Choose publish date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "date",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.createdAt,
                  endDate: values?.createdAt,
                }}
                {...formValidate(errors, "createdAt")}
              />
              <FlexibleSelect
                label={"Status"}
                placeholder={"Choose status"}
                onChange={(e) => handleChange({ target: { name: "status", value: e } })}
                selectedValue={values?.status}
                dropdownData={[
                  { name: "Active", value: "active" },
                  { name: "Inactive", value: "inactive" },
                  { name: "Draft", value: "draft" },
                  { name: "Private", value: "private" },
                ]}
                {...formValidate(errors, "status")}
              />
              <FlexibleSelect
                label={"Payment Type"}
                placeholder={"Choose payment type"}
                onChange={(e) => handleChange({ target: { name: "paymentType", value: e } })}
                selectedValue={values?.price?.paymentType}
                dropdownData={[
                  { name: "Fixed", value: "fixed" },
                  { name: "Hourly", value: "hourly" },
                ]}
                {...formValidate(errors, "paymentType")}
              />
              <FlexibleSelect
                label={"Budget Range Type"}
                placeholder={"Choose budget range type"}
                onChange={(e) => handleChange({ target: { name: "rangeType", value: e } })}
                selectedValue={values?.range?.rangeType}
                dropdownData={[
                  { name: "Negotiable", value: "negotiable" },
                  { name: "Auction", value: "auction" },
                  { name: "Per Session", value: "per-session" },
                  { name: "Per Month", value: "per-month" },
                  { name: "One Time", value: "one-time" },
                  { name: "Free", value: "free" },
                ]}
                {...formValidate(errors, "budgetRangeType")}
              />
              <FlexibleSelect
                label={"Select budget"}
                dropdownData={budgetData?.data?.map((item) => {
                  return {
                    name: `$${item?.range?.min} - $${item?.range?.max}`,
                    value: item?._id,
                  };
                })}
                selectedValue={budgets}
                onChange={setBudgets}
                placeholder="Choose budgets"
              />
              <FlexibleSelect
                label={"Select category"}
                isSearch={true}
                dropdownData={categoryData?.data?.map((item) => {
                  return {
                    name: item?.content?.[currentLang]?.title,
                    value: item?._id,
                  };
                })}
                selectedValue={categories}
                onChange={setCategories}
                type="multiple"
                placeholder="Choose category"
              />
              <Tags
                label="Tags"
                placeholder="Enter tags"
                options={tagsData?.data?.map((item) => ({
                  label: item?.content?.[userLang]?.title,
                  _id: item._id,
                }))}
                selectedItems={tags}
                setSelectedItems={setTags}
                tagsType="input"
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManageFreelancer;
